import { UsersIcon } from 'lucide-react'
import { ReactNode, useEffect, useRef } from 'react'

import { cn } from '@/lib/ui/style-helpers'

import { TextWithIcon } from '@/components/lemmala-design-system/text-with-icon/text-with-icon'

import { IndexedGroup, RecipientGroup, RecipientMember } from './input-emails.types'

const SuggestionOptionRow = ({
  selected,
  onSelect,
  setRef,
  className,
  selectedClassName,
  children,
}: {
  selected: boolean
  onSelect: () => void
  setRef: (el: HTMLDivElement | null) => void
  className: string
  selectedClassName: string
  children: ReactNode
}) => {
  return (
    <div
      ref={setRef}
      className={cn(className, selected && selectedClassName)}
      onMouseDown={(e) => {
        e.preventDefault()
        onSelect()
      }}
    >
      {children}
    </div>
  )
}

const GroupSuggestionRow = ({
  group,
  selected,
  setRef,
  onSelect,
}: {
  group: RecipientGroup
  selected: boolean
  setRef: (el: HTMLDivElement | null) => void
  onSelect: () => void
}) => {
  return (
    <SuggestionOptionRow
      selected={selected}
      setRef={setRef}
      onSelect={onSelect}
      className='flex w-full flex-col px-3 py-2 text-left hover:bg-gray-50'
      selectedClassName='bg-gray-100'
    >
      <TextWithIcon
        text={
          <div className='flex flex-col gap-1'>
            <div className='text-body-default'>{group.groupName}</div>
            <div className='text-main-foreground-secondary text-body-small'>
              {group.groupMembers.length} contacts
            </div>
          </div>
        }
        icon={<UsersIcon className='mt-0.5 size-3.5 text-main-foreground-tertiary' />}
        className='items-start'
      />
    </SuggestionOptionRow>
  )
}

const MemberSuggestionRow = ({
  member,
  selected,
  setRef,
  onSelect,
}: {
  member: RecipientMember
  selected: boolean
  setRef: (el: HTMLDivElement | null) => void
  onSelect: () => void
}) => {
  return (
    <SuggestionOptionRow
      selected={selected}
      setRef={setRef}
      onSelect={onSelect}
      className='flex w-full cursor-pointer flex-col items-start gap-0 px-3 py-2 text-left hover:bg-main-background-muted'
      selectedClassName='bg-main-background-muted'
    >
      <div className='text-body-default'>{member.name || member.email}</div>
      {member.name && (
        <div className='text-main-foreground-secondary text-body-small'>{member.email}</div>
      )}
    </SuggestionOptionRow>
  )
}

export const RecipientsSuggestionsList = ({
  highlightedIndex,
  indexedGroups,
  onSelectGroup,
  onSelectMember,
}: {
  highlightedIndex: number
  indexedGroups: IndexedGroup[]
  onSelectGroup: (emails: string[]) => void
  onSelectMember: (email: string) => void
}) => {
  const optionRefs = useRef<(HTMLDivElement | null)[]>([])

  useEffect(() => {
    const highlightedElement = optionRefs.current[highlightedIndex]
    if (highlightedElement) highlightedElement.scrollIntoView({ block: 'nearest' })
  }, [highlightedIndex])

  return (
    <div className='max-h-72 overflow-auto py-1'>
      {indexedGroups.map(({ group, groupIdx, memberIdxs }) => {
        const remainingToAdd = group.groupMembers.map((member) => member.email)
        return (
          <div key={group.groupName} className='cursor-pointer border-b last:border-b-0'>
            <GroupSuggestionRow
              group={group}
              selected={highlightedIndex === groupIdx}
              setRef={(el) => {
                optionRefs.current[groupIdx] = el
              }}
              onSelect={() => onSelectGroup(remainingToAdd)}
            />
            {group.groupMembers.map((member, memberIndex) => {
              const memberIdx = memberIdxs[memberIndex]
              return (
                <MemberSuggestionRow
                  key={member.email}
                  member={member}
                  selected={highlightedIndex === memberIdx}
                  setRef={(el) => {
                    optionRefs.current[memberIdx] = el
                  }}
                  onSelect={() => onSelectMember(member.email)}
                />
              )
            })}
          </div>
        )
      })}
    </div>
  )
}
